import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';

import { VODCSSModules, VODBiEvents } from 'shared/utils/wix-connect';
import { getNewRelic } from 'shared/analytics/new-relic';
import {
  getAllSettings,
  getSiteUrl,
  getRequestedWidgetSize,
  isPreviewHover,
  isVideoListPreviewHover,
  canShowChannelShare,
  canShowVideoList,
  canShowSignIn,
  canShowChannelInfo,
  isCompactPlayInFrame,
} from 'shared/selectors/app-settings';

import { isVisible as canShowSubscriptionButton } from 'widget/components/overlay-texts/channel/subscribe-button-text';

import { openFullScreenOverlay } from 'shared/utils/open-overlay';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import { playerSize } from 'widget/utils/videos-sizes';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { isVideoPlaying, isVideoPaused } from 'widget/selectors/video-playback-status';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { isActionBarVisible } from 'widget/views/desktop/compact/ui-selectors/ui-selectors';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';
import { COMPACT_VIDEOS_IN_ROW, COMPACT_NUMBER_OF_ROWS } from 'shared/constants/compact-layout';
import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, resetPlaybackStatus } from 'widget/redux/client/actions/change-playback-status';
import { openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';

import BaseView from 'widget/views/_base';

import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import ResizeWidgetByContent from 'widget/views/desktop/compact/components/resize-widget-by-content/resize-widget-by-content';

import MobilePlayer from 'widget/components/players/mobile-player';
import DesktopPlayer from 'widget/components/players/desktop-player';

import Videos from 'widget/views/desktop/compact/components/videos/videos';
import ClosableOverlay from 'widget/views/desktop/compact/components/closable-overlay/closable-overlay';
import ShareChannel from 'widget/views/desktop/compact/components/share-channel/share-channel';
import { ChannelOverlay, VideoOverlay } from 'widget/views/desktop/compact/components/player-overlay/overlay';
import ChanelActions from 'widget/views/desktop/compact/components/channel-actions/channel-actions';
import ActionBar from 'widget/views/desktop/compact/components/action-bar/action-bar';
import Nav from 'widget/components/nav/nav';
import Icon from 'shared/components/icon/icon';

import InFrameActionCallbacks from './actions/overlay-action-callbacks/in-frame';
import InFrameMobileActionCallbacks from './actions/overlay-action-callbacks/in-frame-mobile';
import InOverlayActionCallbacks from './actions/overlay-action-callbacks/in-overlay';
import ChannelActionCallbacks from './actions/channel-action-callbacks';
import PlayerActionCallbacks from './actions/player-action-callbacks';

import Animate from 'widget/views/desktop/compact/components/animate/animate';

import styles from './compact.styl';


const DEFAULT_STATE = {
  canShowChannelCover: true,
  areAllVideosVisible: false,
  isShareOverlayVisible: false,
  isInFullScreen: false,
};

const IS_MOBILE_DEVICE = isMobile() || isTablet();
const MIN_WIDGET_WIDTH = 400;
const MAX_WIDGET_WIDTH = 980;

const ACTION_BAR_OPENED_HEIGHT = 60;
const ACTION_BAR_CLOSED_HEIGHT = 40;


function OverlayActionsCallbacks(store) {
  if (IS_MOBILE_DEVICE) {
    return InFrameMobileActionCallbacks;
  }

  if (isCompactPlayInFrame(store)) {
    return InFrameActionCallbacks;
  }

  return InOverlayActionCallbacks;
}


@connect((store, ownProps) => ({
  isVideoPaused: isVideoPaused(store),
  isVideoPlaying: isVideoPlaying(store),
  isPlayInFrame: isCompactPlayInFrame(store),
  mainVideoId: getMainVideoId(store, DATA_KEY),
  videoIdsByPageNumber: ownProps.paginatedChannelVideos.idChunks,
  currentVideosPageNumber: ownProps.paginatedChannelVideos.currentUIPage,
  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  goToPrevVideosPage: ownProps.paginatedChannelVideos.goBack,
  goToNextVideosPage: ownProps.paginatedChannelVideos.goForward,

  appSettings: getAllSettings(store),
  siteUrl: getSiteUrl(store),
  videoByIds: getVideosGroupedByIds(store),
  requestedWidgetSize: getRequestedWidgetSize(store),

  canShowChannelCover: !store.isVideosTouched,
  canShowChannelInfo: canShowChannelInfo(store),
  canShowChannelShare: canShowChannelShare(store),
  canShowVideoList: canShowVideoList(store),
  canShowSignIn: canShowSignIn(store),
  canShowSubscriptionButton: canShowSubscriptionButton({ channel: ownProps.channelData }),
  isPreviewHover: isPreviewHover(store),
  isVideoListPreviewHover: isVideoListPreviewHover(store),
  isActionBarVisible: isActionBarVisible(store),

  OverlayActionsCallbacks: OverlayActionsCallbacks(store),
}), {
  selectVideo,
  resetSelectedVideo,
  resetPlaybackStatus,
})
@VODBiEvents()
@VODCSSModules(styles)
export default class CompactView extends BaseView {
  static displayName = 'CompactView';

  static propTypes = {
    mainVideoId: React.PropTypes.string,
    channelVideoIds: React.PropTypes.arrayOf(React.PropTypes.string),
    prevPageVideosIds: React.PropTypes.arrayOf(React.PropTypes.string),
    nextPageVideosIds: React.PropTypes.arrayOf(React.PropTypes.string),

    isActionBarVisible: React.PropTypes.bool,
    canShowChannelShare: React.PropTypes.bool,
    canShowChannelInfo: React.PropTypes.bool,
    canShowVideoList: React.PropTypes.bool,
    canShowSignIn: React.PropTypes.bool,
    isPreviewHover: React.PropTypes.bool,
    isVideoListPreviewHover: React.PropTypes.bool,

    OverlayActionsCallbacks: React.PropTypes.func,

    resetPlaybackStatus: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = DEFAULT_STATE;

    getNewRelic().setCustomAttribute('layout', 'compact');
  }

  componentWillReceiveProps(nextProps) {
    super.componentWillReceiveProps(nextProps);

    const isVideoPlayingChanged = nextProps.isVideoPlaying !== this.props.isVideoPlaying;
    const isMainVideoIdChanged = nextProps.mainVideoId !== this.props.mainVideoId;

    if (this.state.areAllVideosVisible && (
        isVideoPlayingChanged && nextProps.isVideoPlaying ||
        isMainVideoIdChanged
      )) {
      this.hideAllVideos();
    }
  }

  @autobind
  reset() {
    this.setState(DEFAULT_STATE);
    this.props.resetSelectedVideo();
    this.mainVideoList.toFirstPage();
  }

  @autobind
  showAllVideos() {
    this.props.resetPlaybackStatus();
    this.setState({
      areAllVideosVisible: true,
    });
  }

  @autobind
  hideAllVideos() {
    this.setState({
      areAllVideosVisible: false,
    });
  }

  @autobind
  toggleShareOverlay() {
    this.setState({
      isShareOverlayVisible: !this.state.isShareOverlayVisible,
    });
  }

  @autobind
  hideShareOverlay() {
    this.setState({
      isShareOverlayVisible: false,
    });
  }

  get shareButtons() {
    const { appSettings, channelData } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!isShareOverlayVisible) {
      return null;
    }

    return (
      <ShareChannel channelData={channelData}
                    appSettings={appSettings} />
    );
  }

  get shareChannelOverlay() {
    const { canShowChannelShare } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!canShowChannelShare) {
      return null;
    }

    return (
      <Animate styleName="share-channel-overlay"
               type="fade-in-out"
               when={isShareOverlayVisible}>
        <ClosableOverlay onClose={this.hideShareOverlay}>
          {this.shareButtons}
        </ClosableOverlay>
      </Animate>
    );
  }

  get playerOverlayNav() {
    const { canShowVideoList, isActionBarVisible, nextVideoId, prevVideoId } = this.props;
    const { selectNextVideo, selectPrevVideo } = this;

    if (!canShowVideoList && isActionBarVisible) {
      return null;
    }

    return (
      <Nav prevButtonClassName={styles['nav-button-prev']}
           nextButtonClassName={styles['nav-button-next']}
           isPrevVisible={Boolean(prevVideoId)}
           isNextVisible={Boolean(nextVideoId)}
           onNextClick={selectNextVideo}
           onPrevClick={selectPrevVideo} />
    );
  }

  get playerOverlay() {
    const { width, height } = this.playerContentSize;
    const mainVideo = this.mainVideo;

    if (!mainVideo) {
      // TODO Move to component
      // each initial state need to be a single component
      return this.noVideosPlayerContent;
    }

    const { channelData, OverlayActionsCallbacks, currentSiteUser } = this.props;

    const Overlay = this.isChannelCoverShown ? ChannelOverlay : VideoOverlay;

    return (
      <div styleName="player-container">
        {this.playerOverlayNav}
        <OverlayActionsCallbacks isMainVideo
                                 channelId={channelData.id}
                                 videoId={mainVideo.id}>
          <Overlay styleName="player-overlay"
                   currentSiteUser={_.get(currentSiteUser, 'data')}
                   width={width}
                   height={height}
                   channelData={channelData}
                   videoItem={mainVideo} />
        </OverlayActionsCallbacks>
      </div>
    );
  }

  get player() {
    const videoItem = this.mainVideo;

    if (!videoItem) {
      return null;
    }

    const { width, height } = this.playerContentSize;
    const { channelData, isPlayInFrame, isVideoPlaying, currentSiteUser } = this.props;

    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser.data);
    if (!isPlayAllowed) {
      return null;
    }

    const playerProps = {
      videoItem,
      channelData,
      width,
      height,
      openPreviewPrompt,
      onFullScreenChanged: this.setFullScreenState,
      paused: !isVideoPlaying,
    };

    const callbackProps = {
      channelId: channelData.id,
      videoId: videoItem.id,
    };

    if (IS_MOBILE_DEVICE) {
      return (
        <PlayerActionCallbacks {...callbackProps}>
          <MobilePlayer {...playerProps} />
        </PlayerActionCallbacks>
      );
    }

    if (isPlayInFrame && (isVideoPlaying || isVideoPaused)) {
      return (
        <PlayerActionCallbacks {...callbackProps}>
          <DesktopPlayer {...playerProps} />
        </PlayerActionCallbacks>
      );
    }

    return null;
  }

  get playerContentSize() {
    const { canShowVideoList } = this.props;

    if (!canShowVideoList) {
      return this.playerSize;
    }

    const { width, height } = this.playerSize;
    return {
      width,
      height: height - (this.actionBarHeight - ACTION_BAR_CLOSED_HEIGHT),
    };
  }

  get playerSize() {
    const { requestedWidgetSize } = this.props;
    const width = Math.min(Math.max(requestedWidgetSize.width, MIN_WIDGET_WIDTH), MAX_WIDGET_WIDTH);
    return playerSize(width, 0);
  }

  get actionBarHeight() {
    return this.areActionsVisible ? ACTION_BAR_OPENED_HEIGHT : ACTION_BAR_CLOSED_HEIGHT;
  }

  get thumbnailSize() {
    const { width, height } = this.playerSize;

    return {
      width: width / COMPACT_VIDEOS_IN_ROW,
      height: (height - (ACTION_BAR_OPENED_HEIGHT - ACTION_BAR_CLOSED_HEIGHT)) / COMPACT_NUMBER_OF_ROWS,
    };
  }

  get videos() {
    const {
      channelData,
      goToPrevVideosPage,
      goToNextVideosPage,
      videoIdsByPageNumber,
      currentVideosPageNumber,
      canShowVideoList,
      OverlayActionsCallbacks,
    } = this.props;

    if (!canShowVideoList) {
      return null;
    }

    return (
      <Videos containerWidth={this.playerContentSize.width}
              channelData={channelData}
              videoIdsByPageNumber={videoIdsByPageNumber}
              currentVideosPageNumber={currentVideosPageNumber}
              onSlideToPrev={goToPrevVideosPage}
              onSlideToNext={goToNextVideosPage}
              OverlayActionsCallbacks={OverlayActionsCallbacks}
              thumbnailSize={this.thumbnailSize} />
    );
  }

  get actionBarNav() {
    const { canShowVideoList, nextVideoId, prevVideoId } = this.props;
    const { selectNextVideo, selectPrevVideo } = this;

    if (canShowVideoList) {
      return null;
    }

    return (
      <Nav nextButtonClassName={styles['action-bar-nav-button']}
           prevButtonClassName={styles['action-bar-nav-button']}
           isPrevVisible={Boolean(prevVideoId)}
           isNextVisible={Boolean(nextVideoId)}
           onNextClick={selectNextVideo}
           onPrevClick={selectPrevVideo}
           dataHook="actionBarNav"
           customArrowIcon={(
             <Icon className={styles['action-bar-nav-icon']}
                   name="arrow-strip" />
           )} />
    );
  }

  get actionBar() {
    const { isActionBarVisible } = this.props;

    if (!isActionBarVisible) {
      return null;
    }

    return (
      <ActionBar height={this.actionBarHeight}
                 nav={this.actionBarNav}
                 areActionsVisible={this.areActionsVisible}
                 actions={this.actions}
                 onMenuClick={this.showAllVideos}
                 onButtonsClick={this.hideShareOverlay} />
    );
  }

  @autobind
  handleShareRequest(clickEvent) {
    clickEvent.stopPropagation();
    this.toggleShareOverlay();
  }

  get actions() {
    const {
      channelData,
      canShowVideoList,
      canShowSignIn,
      canShowChannelShare,
      canShowChannelInfo,
      canShowSubscriptionButton,
    } = this.props;

    return (
      // TODO onUserLogin, onUserLogOut - etc. make BaseAccountButton dumb to only
      // call callbacks provided to it aka onUserLoginRequested...
      <ChannelActionCallbacks channelId={channelData.id}>
        <ChanelActions channelData={channelData}
                       canShowSignIn={canShowSignIn}
                       canShowChannelShare={canShowChannelShare}
                       canShowChannelInfo={canShowChannelInfo}
                       canShowCloseButton={canShowVideoList}
                       canShowSubscriptionButton={canShowSubscriptionButton}

                       onShareRequest={this.handleShareRequest}
                       onCloseRequest={this.hideAllVideos}
                       onLogInRequest={this.showLogInForm}
                       onLogOutRequest={this.logOut}
                       onAccountInfoAtOverlayRequest={this.openAccountInfoAtOverlay} />
      </ChannelActionCallbacks>
    );
  }

  get isVideoListVisible() {
    const { areAllVideosVisible } = this.state;
    const { isVideoListPreviewHover, isPreviewHover, canShowVideoList } = this.props;
    if (!canShowVideoList) {
      return false;
    }
    return areAllVideosVisible || isVideoListPreviewHover || isPreviewHover;
  }

  get areActionsVisible() {
    const { canShowVideoList } = this.props;
    return !canShowVideoList || this.isVideoListVisible;
  }

  get isVideosLoaded() {
    const { videoByIds, paginatedChannelVideos } = this.props;
    return !paginatedChannelVideos.isFetching || !_.isEmpty(videoByIds);
  }

  @autobind()
  processDataFromStorage(data) {
    // TODO: get rid of this in view
    const { selectedVideoId, openOverlay, autoplay } = data;
    const { selectVideo, resetSelectedVideo, channelData, isPlayInFrame } = this.props;

    if (!selectedVideoId) {
      resetSelectedVideo();
      return;
    }

    if ((isPlayInFrame || IS_MOBILE_DEVICE) && autoplay) {
      playVideo(selectedVideoId);
    } else {
      selectVideo(selectedVideoId);
    }

    if (!isPlayInFrame && openOverlay) {
      openFullScreenOverlay(`#/channel/${channelData.id}/video/${selectedVideoId}/${autoplay ? 'play' : ''}`);
    }
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { isVideoPlaying, isVideoPaused } = this.props;

    return (
      <ResizeWidgetByContent>
        <section styleName="container"
                 ref={this.setContainerEl}
                 className="qa-widget-container">
          <UpdateWidgetSize disabled={this.state.isInFullScreen} />
          <ReloadStorage onReload={this.processDataFromStorage} />

          <div styleName="content"
               style={this.playerContentSize}>
            <Animate type="fade-in"
                     styleName="videos-in-animation"
                     when={this.isVideoListVisible}>
              {this.videos}
            </Animate>
            <Animate type="fade-in"
                     dataHook="playerContent"
                     when={!this.isVideoListVisible}>
              {this.player}
              {!isVideoPlaying && !isVideoPaused && this.playerOverlay}
            </Animate>
            {this.shareChannelOverlay}
          </div>

          {this.actionBar}

          <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                         onSale={this.setCurrentVideoFromPayment}
                         onSubscription={this.reset}
                         onSubscriptionCancel={this.reset} />
        </section>
      </ResizeWidgetByContent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/compact/compact.js